import { CartAddEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

/**
 * Bundle selector (PRD §7.1).
 *
 * Reveals N product slots for the selected tier and posts every slot as its own cart line in a
 * single request, so per-variant inventory stays honest and the native quantity-tier discounts see
 * the real item count.
 *
 * Prices are never computed here. Every amount on screen was rendered by Liquid through the `money`
 * filter; this only shows and hides what Liquid already produced (PRD §P2).
 */

/** How long to wait for the free gift to settle before giving up and going to checkout anyway. */
const GIFT_SETTLE_TIMEOUT = 2000;

/** Entrance animation is 200ms; clear comfortably after it should have finished. */
const ENTER_CLEANUP = 400;

/** Fires the §8 instrumentation the tier take-rate hypothesis depends on. */
function track(event, payload) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...payload });

  if (typeof window.fbq === 'function') window.fbq('trackCustom', event, payload);
}

class SonrisaBundle extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-bundle-form]');
    this.tiers = Array.from(this.querySelectorAll('[data-tier]'));
    this.slots = Array.from(this.querySelectorAll('[data-slot]'));
    this.submit = this.querySelector('[data-bundle-submit]');
    this.submitText = this.querySelector('[data-bundle-submit-text]');
    this.hint = this.querySelector('[data-bundle-hint]');

    if (!this.form || !this.tiers.length || !this.slots.length) return;

    this.defaultButtonText = this.submitText?.textContent?.trim() ?? 'Add to cart';

    for (const tier of this.tiers) {
      tier.addEventListener('change', () => this.#onTierChange());
    }

    for (const slot of this.slots) {
      slot.addEventListener('change', (event) => {
        if (event.target.matches('[data-slot-option]')) this.#onSlotChange(slot);
      });
    }

    this.form.addEventListener('submit', this.#onSubmit);

    this.#onTierChange({ silent: true });

    for (const slot of this.slots) this.#revealChosen(slot);

    track('bundle_widget_viewed', {
      product_id: this.dataset.productId,
      default_tier: this.#selectedTier?.dataset.qty,
    });
  }

  get #selectedTier() {
    return this.tiers.find((tier) => tier.checked) ?? null;
  }

  get #quantity() {
    return Number(this.#selectedTier?.dataset.qty ?? 1);
  }

  /** Slots that are actually part of the current tier. */
  get #activeSlots() {
    return this.slots.slice(0, this.#quantity);
  }

  #onTierChange({ silent = false } = {}) {
    const quantity = this.#quantity;

    // A single unit needs no colour picker: slot 1 is already the product being viewed.
    const visibleCount = quantity === 1 ? 0 : quantity;

    // Clear the entrance marker up front rather than waiting on animationend, which does not
    // fire in throttled or background tabs and would leave the class stuck on — blocking the
    // animation from ever replaying. Re-adding it below is enough to restart the animation,
    // because a revealed slot is coming back from display:none.
    for (const slot of this.slots) slot.classList.remove('sonrisa-slot--enter');

    this.slots.forEach((slot, index) => {
      const shown = index < visibleCount;
      const revealed = shown && slot.hidden;

      // `hidden` keeps the slot out of the layout, the tab order and the accessibility tree.
      slot.hidden = !shown;

      if (!shown) {
        slot.classList.remove('sonrisa-slot--missing');
        return;
      }

      // Only a slot that just appeared gets the entrance; slots already on screen must not
      // re-animate every time the tier changes.
      if (revealed) {
        slot.classList.add('sonrisa-slot--enter');

        // Cleared on a timer as well as on the next tier change. The entrance starts from
        // opacity 0, so a stalled animation — a throttled or background tab — would otherwise
        // leave the slot invisible. Decoration must never be able to hide the content.
        setTimeout(() => slot.classList.remove('sonrisa-slot--enter'), ENTER_CLEANUP);
      }
    });

    this.#validate();

    if (silent) return;

    const tier = this.#selectedTier;
    track('bundle_tier_selected', {
      product_id: this.dataset.productId,
      tier: tier?.value,
      qty: quantity,
      price: Number(tier?.dataset.price ?? 0),
    });
  }

  /** @param {Element} slot */
  #chosen(slot) {
    return slot.querySelector('[data-slot-option]:checked');
  }

  /**
   * Scrolls a slot's chosen swatch into view inside its own strip.
   *
   * Slot 1 is preselected to the product being viewed, which can sit anywhere in fourteen
   * colours — often past the right edge, so the slot looks unset until you scroll. Only the
   * strip is scrolled, never the page.
   *
   * @param {Element} slot
   */
  #revealChosen(slot) {
    const chosen = this.#chosen(slot);
    const strip = slot.querySelector('[data-slot-swatches]');
    const swatch = chosen?.closest('.sonrisa-swatch');
    if (!strip || !swatch) return;

    const target = swatch.offsetLeft - strip.clientWidth / 2 + swatch.offsetWidth / 2;
    strip.scrollLeft = Math.max(0, target);
  }

  /** Keeps the slot's caption in step with the swatch the shopper picked. */
  #onSlotChange(slot) {
    const chosen = this.#chosen(slot);
    const caption = slot.querySelector('[data-slot-chosen]');

    if (caption && chosen) caption.textContent = chosen.dataset.title ?? '';

    this.#validate();
  }

  #validate() {
    const missing = this.#activeSlots.filter((slot) => !this.#chosen(slot));

    for (const slot of this.#activeSlots) {
      slot.classList.toggle('sonrisa-slot--missing', missing.includes(slot));
    }

    const ready = missing.length === 0;
    if (this.submit) this.submit.disabled = !ready;

    // A disabled button with no explanation is a dead end — say which slot is empty.
    if (this.hint) {
      this.hint.textContent = ready
        ? ''
        : `Pick a colour for ${missing.length === 1 ? 'the remaining slot' : `${missing.length} more slots`}.`;
    }

    return ready;
  }

  #onSubmit = (event) => {
    // Without JS this form posts a single unit; from here on we own the submit.
    event.preventDefault();

    if (!this.#validate()) return;

    const items = this.#activeSlots.map((slot) => ({
      id: Number(this.#chosen(slot).value),
      quantity: 1,
    }));

    this.#setBusy(true);

    const sections = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => element.dataset.sectionId)
      .filter((id, index, ids) => id && ids.indexOf(id) === index);

    const body = JSON.stringify({
      items,
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });

    fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }))
      .then((response) => response.json())
      .then((result) => {
        if (result.status) {
          if (this.hint) this.hint.textContent = result.description || result.message;
          return;
        }

        track('bundle_added_to_cart', {
          product_id: this.dataset.productId,
          tier: this.#selectedTier?.value,
          variant_ids: items.map((item) => item.id),
          value: Number(this.#selectedTier?.dataset.price ?? 0),
        });

        if (result.sections) {
          for (const [sectionId, html] of Object.entries(result.sections)) {
            try {
              morphSection(sectionId, html);
            } catch {
              // Section is not on this page. Nothing to morph.
            }
          }
        }

        if (this.dataset.afterAdd === 'checkout') {
          // Settle the free gift first, or going straight to checkout would skip it entirely.
          // Capped, because a stalled gift reconcile must never hold a shopper out of checkout.
          return this.#settleGift().then(() => this.#goToCheckout());
        }

        // CartAddEvent opens the drawer, which is what should happen after a deliberate bundle add,
        // and is also what wakes the free-gift reconcile.
        document.dispatchEvent(
          new CartAddEvent({}, this.dataset.productId, {
            source: 'sonrisa-bundle',
            sections: result.sections,
          })
        );
      })
      .catch((error) => {
        console.error('[sonrisa-bundle] add failed', error);
        if (this.hint) this.hint.textContent = 'Something went wrong. Please try again.';
      })
      .finally(() => this.#setBusy(false));
  };

  /**
   * Sends the shopper straight to checkout.
   *
   * Dynamic checkout buttons post the product form and skip the cart, so they can only ever carry
   * one variant — a mixed bundle cannot travel that way. Posting the cart to `routes.cart_url` with
   * a `checkout` field is the same submit the theme's own checkout button performs, so it stays
   * correct under locale-prefixed URLs, and the automatic tier discount is evaluated and displayed
   * in the checkout summary as usual.
   */
  #settleGift() {
    const pending = window.sonrisaGift?.reconcileNow?.();
    if (!pending) return Promise.resolve();

    return Promise.race([pending, new Promise((resolve) => setTimeout(resolve, GIFT_SETTLE_TIMEOUT))]).catch(
      () => undefined
    );
  }

  #goToCheckout() {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = this.dataset.cartUrl || Theme.routes.cart_url;
    form.hidden = true;

    const field = document.createElement('input');
    field.type = 'hidden';
    field.name = 'checkout';
    field.value = '';
    form.appendChild(field);

    document.body.appendChild(form);
    form.submit();
  }

  #setBusy(busy) {
    if (!this.submit) return;

    this.submit.disabled = busy;
    this.submit.setAttribute('aria-busy', String(busy));
    if (this.submitText) this.submitText.textContent = busy ? 'Adding…' : this.defaultButtonText;
  }
}

if (!customElements.get('sonrisa-bundle')) {
  customElements.define('sonrisa-bundle', SonrisaBundle);
}
